'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { logAudit } from '@/lib/auditService';

export type MemberRole = 'owner' | 'admin' | 'staff' | 'viewer';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl?: string;
  companyId: string | null;
  isSuperAdmin?: boolean;
}

export interface CompanyInfo {
  id: string;
  name: string;
  slug: string | null;
  ownerIdFk: string | null;
  logoUrl?: string | null;
  currency?: string;
  timezone?: string;
  notificationsEnabled?: boolean;
  notificationsEmail?: string | null;
}

export interface AuthContextValue {
  user: any | null;
  session: any | null;
  profile: UserProfile | null;
  company: CompanyInfo | null;
  role: MemberRole | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  isSuperAdmin: boolean;
  signUp: (
    email: string,
    password: string,
    meta: { fullName: string; companyName?: string; invitationToken?: string }
  ) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [role, setRole] = useState<MemberRole | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  const loadProfile = useCallback(
    async (userId: string) => {
      try {
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (!profileData) return;

        const p: UserProfile = {
          id: profileData.id,
          email: profileData.email,
          fullName: profileData.full_name,
          avatarUrl: profileData.avatar_url,
          companyId: profileData.company_id,
          isSuperAdmin: profileData.is_super_admin ?? false,
        };
        setProfile(p);

        if (profileData.company_id) {
          const { data: companyData } = await supabase
            .from('companies')
            .select('*')
            .eq('id', profileData.company_id)
            .single();

          if (companyData) {
            setCompany({
              id: companyData.id,
              name: companyData.name,
              slug: companyData.slug,
              ownerIdFk: companyData.owner_id_fk,
              logoUrl: companyData.logo_url ?? null,
              currency: companyData.currency ?? 'USD',
              timezone: companyData.timezone ?? 'UTC',
              notificationsEnabled: companyData.notifications_enabled ?? true,
              notificationsEmail: companyData.notifications_email ?? null,
            });
          }

          const { data: memberData } = await supabase
            .from('company_members')
            .select('role')
            .eq('user_id', userId)
            .eq('company_id', profileData.company_id)
            .eq('is_active', true)
            .single();

          if (memberData) {
            setRole(memberData.role as MemberRole);
          }
        }
      } catch {
        // Profile load failure is non-fatal
      }
    },
    [supabase]
  );

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadProfile(user.id);
  }, [user, loadProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setCompany(null);
        setRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    meta: { fullName: string; companyName?: string; invitationToken?: string }
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: meta.fullName,
          company_name: meta.companyName || '',
          invitation_token: meta.invitationToken || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    // Log login event after profile loads
    if (data.user) {
      setTimeout(async () => {
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('full_name, company_id')
          .eq('id', data.user!.id)
          .single();

        if (profileData?.company_id) {
          await logAudit({
            companyId: profileData.company_id,
            userId: data.user!.id,
            userEmail: email,
            userName: profileData.full_name || email,
            action: 'login',
            entityType: 'session',
            description: `${profileData.full_name || email} signed in`,
          });
        }
      }, 1500);
    }

    return data;
  };

  const signOut = async () => {
    if (profile && company) {
      await logAudit({
        companyId: company.id,
        userId: profile.id,
        userEmail: profile.email,
        userName: profile.fullName,
        action: 'logout',
        entityType: 'session',
        description: `${profile.fullName} signed out`,
      });
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setCompany(null);
    setRole(null);
    router.push('/sign-up-login');
  };

  const isOwner = role === 'owner';
  const isAdmin = role === 'owner' || role === 'admin';
  const canEdit = role === 'owner' || role === 'admin' || role === 'staff';
  const isSuperAdmin = profile?.isSuperAdmin ?? false;

  const value: AuthContextValue = {
    user,
    session,
    profile,
    company,
    role,
    loading,
    isOwner,
    isAdmin,
    canEdit,
    isSuperAdmin,
    signUp,
    signIn,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
